import * as React from "react";
import { Stack } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { HealthBadge } from "@/components/dashboard/HealthBadge";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { useMarketDataProviders } from "@/hooks/queries/useMarketDataProviders";
import { useBrokerStatus } from "@/hooks/queries/useBrokerStatus";
import { useSyncStatus } from "@/hooks/queries/useSyncStatus";

interface ProviderStatusPayload {
  availability: Record<string, boolean>;
}

interface BrokerStatusPayload {
  statuses: Record<string, string>;
}

interface SyncStatusPayload {
  has_run: boolean;
  summary: { results: Array<{ resource: string; is_success: boolean }> } | null;
}

/** Reuses Sprint 3.1/3.2's `useBrokerStatus`/`useSyncStatus` hooks alongside the new `useMarketDataProviders` hook - no logic duplicated. */
export function MarketStatusCard(): React.JSX.Element {
  const providersQuery = useMarketDataProviders();
  const brokerStatusQuery = useBrokerStatus();
  const syncQuery = useSyncStatus();

  return (
    <StatusCard title="Market Status">
      <Stack spacing={1.5}>
        <QueryStateBoundary query={brokerStatusQuery}>
          {(payload) => {
            const statuses = (payload as unknown as BrokerStatusPayload).statuses;
            const first = Object.values(statuses)[0] ?? null;
            return <MetricCard label="Provider Status" render={<HealthBadge status={first} />} />;
          }}
        </QueryStateBoundary>
        <QueryStateBoundary query={brokerStatusQuery}>
          {(payload) => {
            const statuses = (payload as unknown as BrokerStatusPayload).statuses;
            const summary = Object.entries(statuses)
              .map(([id, status]) => `${id}: ${status}`)
              .join(", ");
            return <MetricCard label="Connection Status" value={summary} />;
          }}
        </QueryStateBoundary>
        <QueryStateBoundary query={providersQuery}>
          {(payload) => {
            const availability = (payload as unknown as ProviderStatusPayload).availability;
            const summary = Object.entries(availability)
              .map(([id, available]) => `${id}: ${available ? "available" : "unavailable"}`)
              .join(", ");
            return <MetricCard label="Market Data Status" value={summary} />;
          }}
        </QueryStateBoundary>
        <QueryStateBoundary query={syncQuery}>
          {(payload) => {
            const sync = payload as unknown as SyncStatusPayload;
            const status = !sync.has_run
              ? "not_run"
              : sync.summary?.results.every((r) => r.is_success)
                ? "ok"
                : "degraded";
            return <MetricCard label="Synchronization Status" render={<HealthBadge status={status} />} />;
          }}
        </QueryStateBoundary>
      </Stack>
    </StatusCard>
  );
}
